import {
  type CellAddress,
  useBoardColumnPaging,
  useBoardData,
  useBoardItemPaging,
  useBoardLabels,
  useBoardRowPaging,
} from '@github-ui/board-core'
import {clsx} from 'clsx'

import {useAnnounceOnce} from './announce-once'
import styles from './BoardPaging.module.css'
import {BoardCardSkeletons, BoardColumnSkeleton, BoardRowSkeleton} from './BoardSkeletons'

/**
 * The three paging directions, as three components that never conflate them.
 *
 * There is no control to press. Each of these draws a ghost at the end of what is loaded
 * when, and only when, that direction says it has more, and reaching the ghost is what
 * asks for the next page. Nothing in here fetches: it reports, and the next page arrives
 * as new `data`.
 *
 * A ghost is not a loading skeleton. A skeleton shimmers because something is happening;
 * a ghost is still, because nothing is, and it is only saying that something else
 * exists. Using the skeleton for both would leave a board that is doing nothing looking
 * permanently busy, and put one unstoppable animation per cell on screen.
 *
 * A ghost says so in words rather than in a shape. An empty dashed box would carry the
 * whole message in a boundary, and no muted border token draws one at the contrast a
 * graphic has to meet, so a reader with low vision would be told nothing at all. On this
 * board of all boards a dashed box is also what a drop target looks like. Words cost the
 * same, read the same to everybody, and survive forced colours.
 */

export type BoardItemPagingProps = {
  address: CellAddress
  className?: string
}

/**
 * More cards in one cell.
 *
 * Renders nothing at all in a cell with nothing more to load, so such a cell mounts no
 * sentinel and costs no observer. Everything it reads is addressed to this cell, so a
 * neighbour paging never puts this one into a loading state.
 */
export function BoardItemPaging({address, className}: BoardItemPagingProps) {
  const {hasMore, isLoading, label, sentinelProps} = useBoardItemPaging(address)
  const {pageSize} = useBoardData<unknown>()
  const labels = useBoardLabels()

  useAnnounceOnce(labels.loadingItems, isLoading)

  // A cell can be loading its first page while stating it has no more, because only the
  // server knows yet. It still draws its skeletons; it just has nothing to observe.
  if (!hasMore && !isLoading) return null

  return (
    <div {...sentinelProps} className={clsx(styles.ItemPaging, className)}>
      {isLoading ? (
        <BoardCardSkeletons count={pageSize} />
      ) : (
        <p className={clsx(styles.Ghost, styles.CardGhost)}>{label}</p>
      )}
    </div>
  )
}

export type BoardColumnPagingProps = {
  className?: string
}

/**
 * More columns, or the notice that there will not be any.
 *
 * The two are opposite advice and never appear together: a ghost says keep scrolling,
 * and the notice says stop and narrow the search. Which one shows is settled in
 * `useBoardColumnPaging`, so a caller that states both still gets one.
 */
export function BoardColumnPaging({className}: BoardColumnPagingProps) {
  const {hasMore, isLoading, isTruncated, label, truncationLabel, sentinelProps} = useBoardColumnPaging()
  const labels = useBoardLabels()

  useAnnounceOnce(labels.loadingColumns, isLoading)
  // The advice most worth hearing, and the one nobody asked for: an axis stops being
  // enumerated because of what the reader searched for, and the notice arrives with new
  // data rather than with a keystroke.
  useAnnounceOnce(truncationLabel, isTruncated)

  if (isTruncated) {
    return <p className={clsx(styles.Truncated, styles.ColumnsTruncated, className)}>{truncationLabel}</p>
  }

  if (!hasMore && !isLoading) return null

  return (
    <div {...sentinelProps} className={clsx(styles.ColumnPaging, className)}>
      {isLoading ? <BoardColumnSkeleton /> : <p className={clsx(styles.Ghost, styles.ColumnGhost)}>{label}</p>}
    </div>
  )
}

export type BoardRowPagingProps = {
  className?: string
}

/**
 * More rows, or the notice that there will not be any.
 *
 * A board with no swimlane runs this too. It states `hasMoreRows: false`, which is the
 * ordinary way for an axis to say it is complete, so there is no second path here for
 * the unswimlaned case.
 */
export function BoardRowPaging({className}: BoardRowPagingProps) {
  const {hasMore, isLoading, isTruncated, label, truncationLabel, sentinelProps} = useBoardRowPaging()
  const labels = useBoardLabels()

  useAnnounceOnce(labels.loadingRows, isLoading)
  useAnnounceOnce(truncationLabel, isTruncated)

  if (isTruncated) {
    return <p className={clsx(styles.Truncated, styles.RowsTruncated, className)}>{truncationLabel}</p>
  }

  if (!hasMore && !isLoading) return null

  return (
    <div {...sentinelProps} className={clsx(styles.RowPaging, className)}>
      {isLoading ? <BoardRowSkeleton /> : <p className={clsx(styles.Ghost, styles.RowGhost)}>{label}</p>}
    </div>
  )
}
